import { AppSettingsType } from "AppSettings";
import moment from "moment";
import * as yup from "yup";
import { PremiumType, TimelineType } from "../../../types/types";
import { premiumExpiryDateSchema } from "./premiumExpiryDate";

export const validationObjectConfig = (
  premiumTypeKey,
  t,
  isTradeLicensePlate,
  isInsertOperation,
  appSettings: AppSettingsType,
  oneTimePaymentMethodId?: number,
): any => {
  let yupObject: any = {
    annualGrossPremium: yup.string().required(),
    annualNetPremium: yup.string().required(),

    validFromDate: yup.string().required(),
    reasonForModificationComment: yup.string().trim().max(512).nullable(),
    suspensionDaysLimit: yup.number().required().min(-1),

    insurerGroupPolicyNumber: appSettings?.INCLUDE_GROUP_POLICY
      ? yup.mixed().required().nullable()
      : undefined,
  };
  if (isInsertOperation) {
    yupObject = {
      ...yupObject,
      validToDate: yup.string().required().nullable(),
    };
  }
  if (
    appSettings?.VEHICLE_TIMELINE_TYPES.includes(
      TimelineType.VEHICLE_POLICY_STATUSES,
    )
  ) {
    yupObject = {
      ...yupObject,
      policyStatus: yup.mixed().required(),
    };
  }

  if (
    premiumTypeKey === PremiumType.HULL ||
    premiumTypeKey === PremiumType.HULL_BASIC
  ) {
    const isAustria = appSettings?.COUNTRY_CODE === "Country.AT";
    yupObject = {
      ...yupObject,
      ...(appSettings?.GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED
        ? { garageRiskDiscountPercent: yup.string().required() }
        : {}),
      hullIsBasic: yup.mixed().required(),
      ...(appSettings?.SHOW_HULL_PRODUCT
        ? { hullProduct: yup.mixed().required() }
        : {}),
      hullDeductiblePercent: isAustria
        ? yup.number().required().max(100)
        : yup.number().nullable().max(100),
      hullDeductibleMinAmount: isAustria
        ? yup.string().required()
        : yup.string().nullable(),
      hullDeductibleMaxAmount: isAustria
        ? yup.string().when("hullDeductiblePercent", {
            is: (val) => !val || Number(val) <= 0,
            then: yup.string().required(),
            otherwise: yup.string().nullable(),
          })
        : yup.string().nullable(),
      hullDeductibleComment: isAustria
        ? yup.string().required()
        : yup.string().nullable(),
      // in AT Yes or No has to be selected ("Not defined" = null is not allowed)
      gap: isAustria ? yup.boolean().nullable().required() : yup.boolean().nullable(),
      replacementValue: isAustria ? yup.boolean().nullable().required() : yup.boolean().nullable(),
      grossNegligence: isAustria ? yup.boolean().nullable().required() : yup.boolean().nullable(),
      electroPlusPackage: isAustria ? yup.boolean().nullable().required() : yup.boolean().nullable(),
      isPolicyIssued: yup.boolean().required(),
    };
  }
  if (
    premiumTypeKey === PremiumType.MTPL ||
    premiumTypeKey === PremiumType.HULL ||
    premiumTypeKey === PremiumType.HULL_BASIC ||
    premiumTypeKey === PremiumType.FLEETLEGAL_PRORATA ||
    premiumTypeKey === PremiumType.FLEETLEGAL_CLOSINGDATE ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_PRORATA ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_CLOSINGDATE ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_PRORATA ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE
  ) {
    yupObject = {
      ...yupObject,
      dueDate: yup
        .string()
        .required()
        .test("dueDate", t("bfm.fms.InvalidData.txt"), (value) => {
          const day = (value + "").substring(0, 2);
          const month = (value + "").substring(2, 4);
          return moment("2020-" + month + "-" + day).isValid();
        }),
      insurerPartnerNumber: yup.mixed().required(),
      insurerBranchOffice: yup.mixed(),
      insurerPolicyNumber: yup.string().required(),
      paymentMean: yup.string().required(),
      ...(appSettings?.SUSPENSION_DAYS_INCLUDED && {
        suspensionDays: yup.string().required(),
      }),
      activationDays: yup.string().required(),
      ...(appSettings?.ACTIVATION_DAYS_INCLUDED && {
        suspensionDays: yup.string().required(),
      }),
    };
    if (!isInsertOperation) {
      yupObject = {
        ...yupObject,
        expiryDate: premiumExpiryDateSchema(
          yup.string().required().nullable(),
          yup.string().nullable(),
          appSettings?.COUNTRY_CODE,
          oneTimePaymentMethodId,
        ),
      };
    }
  }

  if (
    premiumTypeKey === PremiumType.FLEETLEGAL_PRORATA ||
    premiumTypeKey === PremiumType.FLEETLEGAL_CLOSINGDATE ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_PRORATA ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_CLOSINGDATE ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_PRORATA ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE
  ) {
    yupObject = {
      ...yupObject,
      premiumType: yup.mixed().required(),
      insurerPolicyNumber: yup.string().required(),
      annualGrossPremium: yup.string().required(),
    };
    if (!isInsertOperation) {
      yupObject = {
        ...yupObject,
        expiryDate: premiumExpiryDateSchema(
          yup.string().required().nullable(),
          yup.string().nullable(),
          appSettings?.COUNTRY_CODE,
          oneTimePaymentMethodId,
        ),
      };
    }
  }

  if (
    premiumTypeKey === PremiumType.MTPL ||
    premiumTypeKey === PremiumType.HULL ||
    premiumTypeKey === PremiumType.HULL_BASIC ||
    premiumTypeKey === PremiumType.HORSE_POWER ||
    premiumTypeKey === PremiumType.FLEETLEGAL_PRORATA ||
    premiumTypeKey === PremiumType.FLEETLEGAL_CLOSINGDATE ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_PRORATA ||
    premiumTypeKey === PremiumType.HULL_ON_LAND_CLOSINGDATE ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_PRORATA ||
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE
  ) {
    yupObject = {
      ...yupObject,
      paymentMethod: yup.mixed().required(),
      paymentMean: yup.mixed().required(),
    };
  }
  if (premiumTypeKey === PremiumType.HORSE_POWER) {
    yupObject = {
      paymentMethod: yup.mixed().required(),
      paymentMean: yup.mixed().required(),

      validFromDate: yup.date().required(),
      reasonForModificationComment: yup.string().trim().max(512).nullable(),
      suspensionDaysLimit: yup.number().required().min(-1),
    };
  }

  if (
    premiumTypeKey === PremiumType.MTPL &&
    !isTradeLicensePlate &&
    appSettings?.VEHICLE_PREMIUM_DOMAINS.includes(PremiumType.HORSE_POWER)
  ) {
    yupObject = {
      ...yupObject,

      motorTaxExclusionReason: yup.mixed().when("isMotorTaxIncluded", {
        is: false,
        then: yup.mixed().required(),
      }),
      motorTaxPaymentMethod: yup.mixed().when("isMotorTaxIncluded", {
        is: true,
        then: yup.mixed().required(),
      }),
      // motorTaxPaymentMean: yup.mixed().when("isMotorTaxIncluded", {
      //   is: true,
      //   then: yup.mixed().required(),
      // }),
      motorTaxSuspensionDaysLimit: yup.number().when("isMotorTaxIncluded", {
        is: true,
        then: yup.number().required().min(-1),
      }),
      motorTaxExclusionComment: yup.string().max(512).nullable(),
    };
  }
  return yupObject;
};
